import { Component, Input, OnInit } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import { GlobalService } from 'src/app/api/global.service';

dayjs.extend(utc);
import { CompaniesContactsSelectPage as ContactsSelectPage } from 'src/app/modals/companies-contacts-select/companies-contacts-select.page';
import { TimeSlotTypes } from '../../time-slot/time-slot.component';
import { AddressBookComponent } from 'src/app/components/address-book/address-book.component';

export enum CalendarEventTypes {
  blocked = 0,
  appointment = 1,
  assigned_work = 2
}

@Component({
  selector: 'app-create-appointment',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.scss'],
})
export class CreateComponent implements OnInit {

  TimeSlotTypes = TimeSlotTypes;
  CalendarEventTypes = CalendarEventTypes;
  loading_data = true;
  loading_dates = false;

  appointment_info = {
    title: '',
    description: '',
    date: '',
    start_time: null,
    start_time_iso: '',
    end_time: null,
    end_time_iso: '',
    type: 1,
    all_day: false,
    selected_users: [],
    in_minutes: 0,
    in_hours: 1,
    selected_length: 60,
    selected_time_slot: '',
    assigned_clients: [],
    assigned_companies: []
  }

  @Input() title = null;
  @Input() description = null;
  @Input() start_time;
  @Input() team_member;
  @Input() force_type = null;
  @Input() email_guid: string = null;
  @Input() event_guid: string = null;
  @Input() is_rescheduling = false;
  @Input() in_assigned_clients: any[] = [];
  @Input() in_assigned_companies: any[] = [];
  @Input() in_assigned_role: any = null;
  @Input() allow_assigned_change = true;
  @Input() in_selected_users: string[] = [];
  @Input() in_length: number = null;
  @Input() in_end_time: Date = null;
  // Opens the modal as a read-only summary (e.g. "View Appointment" from a notification for a
  // client-booked appointment) instead of the normal create/reschedule form. EnableEditing()
  // switches it into the exact same reschedule flow used elsewhere - no separate edit path to
  // maintain. in_users carries display names for the summary so this doesn't need its own
  // team_members lookup just to show who's on the appointment.
  @Input() view_only = false;
  @Input() in_users: any[] = [];
  team_members: any = [];
  bookWith = 'team';
  available_times: any[] = [];

  available_dates_set: Set<string> = new Set();
  calendar_disabled_dates: Date[] = [];
  calendar_date: Date | null = null;
  min_date = new Date();
  displayed_year = new Date().getFullYear();
  displayed_month = new Date().getMonth() + 1;

  constructor(private modalController: ModalController, private apiService: ApiService, public globalService: GlobalService, private alertController: AlertController) {}

  ngOnInit() {
    if (this.force_type != null) {
      this.appointment_info.type = this.force_type;
    }
    if (this.in_assigned_clients?.length) {
      this.appointment_info.assigned_clients = [...this.in_assigned_clients];
    }
    if (this.in_assigned_companies?.length) {
      this.appointment_info.assigned_companies = [...this.in_assigned_companies];
    }
    if (this.title) this.appointment_info.title = this.title;
    if (this.description) this.appointment_info.description = this.description;
    if (this.in_length != null) {
      this.appointment_info.selected_length = this.in_length;
      this.appointment_info.in_hours = Math.floor(this.in_length / 60);
      this.appointment_info.in_minutes = this.in_length % 60;
    }

    const date = new Date(this.start_time);
    this.appointment_info.date = dayjs(this.start_time).format('YYYY-MM-DD');
    const dayOfWeek = date.getDay();
    const hour = date.getHours();
    this.appointment_info.start_time = date;
    const endDate = this.in_end_time ? new Date(this.in_end_time) : new Date(date);
    if (!this.in_end_time) endDate.setHours(endDate.getHours() + 1);
    this.appointment_info.end_time = endDate;

    if (this.event_guid) {
      // Editing (or viewing) an existing event - pre-select its current date/time so a team
      // member who only wants to change the title/description doesn't also have to re-pick a
      // slot. GetData() below (non-view_only) only adds further availability on top of this,
      // it never clears it - see EnableEditing().
      this.calendar_date = date;
      const iso = date.toISOString();
      this.available_dates_set.add(dayjs(date).format('YYYY-MM-DD'));
      this.available_times = [iso];
      this.appointment_info.selected_time_slot = iso;
      if (this.in_length == null) {
        const minutes = Math.round((endDate.getTime() - date.getTime()) / 60000);
        this.appointment_info.selected_length = minutes;
        this.appointment_info.in_hours = Math.floor(minutes / 60);
        this.appointment_info.in_minutes = minutes % 60;
      }
    }

    if (this.view_only) {
      // Same form as editing, just pre-filled with the current values and disabled (see the
      // template's [disabled]="view_only" bindings) - no need to hit team_members or
      // available_slots just to display what's already booked.
      this.team_members = this.in_users || [];
      this.appointment_info.selected_users = this.in_selected_users?.length
        ? [...this.in_selected_users]
        : (this.in_users || []).map((u: any) => u.guid);
      this.loading_data = false;
    } else {
      this.GetData();
    }
    return { dayOfWeek, hour };
  }

  // Drops out of the read-only view into the exact same reschedule flow used elsewhere - keeps
  // the current date/time (seeded into calendar_date/selected_time_slot/available_times above)
  // pre-selected rather than clearing it, so changing just the title/description doesn't also
  // force picking a new slot. GetData() below adds real future availability on top without
  // touching those - the current slot stays selected unless the user actively picks another.
  EnableEditing() {
    this.view_only = false;
    this.is_rescheduling = true;
    this.GetData();
  }

  async SetStartOfDay() {
    this.appointment_info.start_time = dayjs(this.appointment_info.date).startOf('day').toDate();
    this.appointment_info.end_time = dayjs(this.appointment_info.date).endOf('day').toDate();
  }

  async GetData() {
    this.loading_data = true;
    let data: any = this.email_guid
      ? await this.apiService.get(`emails/${this.email_guid}/appointment_request/team_members`)
      : await this.apiService.get('team_members');
    if (data && data.data) {
      this.team_members = data.data;
      if (this.in_selected_users?.length) {
        this.appointment_info.selected_users = [...this.in_selected_users];
      } else if (this.team_member) {
        this.appointment_info.selected_users = [this.team_member.guid];
      }
      if (this.appointment_info.type == CalendarEventTypes.appointment && this.appointment_info.selected_users.length > 0) {
        await this.LoadAvailableDatesForDisplayedMonth();
      }
    }
    this.loading_data = false;
  }

  async OnTeamMembersChange() {
    this.available_dates_set.clear();
    this.calendar_disabled_dates = [];
    this.appointment_info.date = '';
    this.calendar_date = null;
    this.available_times = [];
    this.appointment_info.selected_time_slot = '';
    if (this.appointment_info.type == CalendarEventTypes.appointment && this.appointment_info.selected_users.length > 0) {
      await this.LoadAvailableDatesForDisplayedMonth();
    }
  }

  async GetAvailableDatesFromAPI(start_date: string, end_date: string) {
    if (this.appointment_info.selected_users.length === 0) return;
    this.loading_dates = true;

    const perMemberSets: Set<string>[] = [];
    for (const tm_guid of this.appointment_info.selected_users) {
      const data: any = await this.apiService.get(
        `calendar/available_slots?team_member=${tm_guid}&start_date=${start_date}&end_date=${end_date}&duration=${this.appointment_info.selected_length}`
      );
      const dateSet = new Set<string>();
      const now = new Date();
      if (data?.success && Array.isArray(data.data)) {
        data.data.forEach((slot: { start_time: string }) => {
          if (new Date(slot.start_time) > now) {
            dateSet.add(dayjs(slot.start_time).format('YYYY-MM-DD'));
          }
        });
      }
      perMemberSets.push(dateSet);
    }

    let intersection = perMemberSets[0];
    for (let i = 1; i < perMemberSets.length; i++) {
      intersection = new Set(Array.from(intersection).filter(d => perMemberSets[i].has(d)));
    }
    intersection.forEach(d => this.available_dates_set.add(d));
    this.ComputeDisabledDates();
    this.loading_dates = false;
  }

  async LoadAvailableDatesForDisplayedMonth() {
    const year = this.displayed_year;
    const month = this.displayed_month;
    const today = dayjs();
    const monthStart = dayjs(new Date(year, month - 1, 1));
    const start = monthStart.isBefore(today, 'day') ? today.format('YYYY-MM-DD') : monthStart.format('YYYY-MM-DD');
    const lastDay = new Date(year, month, 0).getDate();
    const end = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    if (start > end) return;
    await this.GetAvailableDatesFromAPI(start, end);
  }

  ComputeDisabledDates() {
    const year = this.displayed_year;
    const month = this.displayed_month;
    const daysInMonth = new Date(year, month, 0).getDate();
    const disabled: Date[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      if (!this.available_dates_set.has(dateStr)) {
        disabled.push(new Date(year, month - 1, d));
      }
    }
    this.calendar_disabled_dates = [...disabled];
  }

  async OnCalendarMonthChange(event: { month: number; year: number }) {
    this.displayed_month = event.month;
    this.displayed_year = event.year;
    await this.LoadAvailableDatesForDisplayedMonth();
  }

  OnCalendarDateSelect(date: Date) {
    this.appointment_info.date = dayjs(date).format('YYYY-MM-DD');
    this.available_times = [];
    this.appointment_info.selected_time_slot = '';
    this.GetAvailableSlotsForSelectedDate();
  }

  async GetAvailableSlotsForSelectedDate() {
    if (this.appointment_info.selected_users.length === 0 || !this.appointment_info.date) {
      this.available_times = [];
      return;
    }
    this.loading_data = true;
    this.available_times = [];
    this.appointment_info.selected_time_slot = '';

    const perMemberSlotMaps: Map<string, string>[] = [];
    for (const tm_guid of this.appointment_info.selected_users) {
      const data: any = await this.apiService.get(
        `calendar/available_slots?team_member=${tm_guid}&start_date=${this.appointment_info.date}&end_date=${this.appointment_info.date}&duration=${this.appointment_info.selected_length}`
      );
      const slotMap = new Map<string, string>();
      if (data?.success && Array.isArray(data.data)) {
        data.data.forEach((slot: { start_time: string }) => slotMap.set(slot.start_time, slot.start_time));
      }
      perMemberSlotMaps.push(slotMap);
    }

    let commonKeys = Array.from(perMemberSlotMaps[0].keys());
    for (let i = 1; i < perMemberSlotMaps.length; i++) {
      commonKeys = commonKeys.filter(k => perMemberSlotMaps[i].has(k));
    }
    const now = new Date();
    this.available_times = commonKeys.sort().filter(k => new Date(k) > now);
    this.loading_data = false;
  }

  isAvailableDate(date: { year: number; month: number; day: number }): boolean {
    // PrimeNG date template passes month as 0-indexed
    const dateStr = `${date.year}-${String(date.month + 1).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
    return this.available_dates_set.has(dateStr);
  }

  isEventInPast(): boolean {
    if (!this.start_time) return false;
    return new Date(this.start_time) < new Date();
  }

  async CancelAppointment() {
    const isBlocked = this.appointment_info.type == CalendarEventTypes.blocked;
    const alert = await this.alertController.create({
      header: isBlocked ? 'Free Up Time' : 'Delete Appointment',
      message: isBlocked
        ? 'Are you sure you would like to free up this time?'
        : 'Are you sure you want to delete this appointment?',
      buttons: [
        { text: 'No', role: 'cancel' },
        {
          text: isBlocked ? 'Yes, Free It Up' : 'Yes, Delete It',
          cssClass: 'alert-button-danger',
          handler: async () => {
            await this.globalService.presentLoading(isBlocked ? 'Freeing up time...' : 'Deleting appointment...', 99999999);
            const data: any = await this.apiService.delete(`calendar/events/${this.event_guid}`, {});
            this.globalService.dismissLoading();
            if (data?.success) {
              this.globalService.presentToastMessage(isBlocked ? 'Time freed up' : 'Appointment deleted', true);
              this.modalController.dismiss({ refresh: true, cancelled: true });
            } else {
              this.globalService.presentToastMessage(data?.message || 'Failed to delete event', false);
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async Close() {
    this.modalController.dismiss();
  }

  async selectedLengthChange() {
    if (this.appointment_info.type != CalendarEventTypes.appointment) return;
    this.available_dates_set.clear();
    this.calendar_disabled_dates = [];
    this.available_times = [];
    this.appointment_info.selected_time_slot = '';
    if (this.appointment_info.selected_users.length > 0) {
      await this.LoadAvailableDatesForDisplayedMonth();
      if (this.appointment_info.date) {
        await this.GetAvailableSlotsForSelectedDate();
      }
    }
  }

  IsValidEvent() {
    let error_message = '';
    if (!this.appointment_info.title || this.appointment_info.title.trim().length === 0) {
      error_message += 'Title is required.<br/>';
    }
    if (!this.appointment_info.date || this.appointment_info.date.trim().length === 0) {
      error_message += 'Date is required.<br/>';
    }
    if (this.appointment_info.type == 1) {
      if (!this.appointment_info.selected_users || this.appointment_info.selected_users.length === 0) {
        error_message += 'At least one team member must be selected for an appointment.<br/>';
      }
      if (!this.appointment_info.selected_time_slot || this.appointment_info.selected_time_slot.trim().length === 0) {
        error_message += 'An available time slot must be selected for an appointment.<br/>';
      }
    }
    return error_message;
  }

  async Create() {
    let start_formatted_UTC: string;
    let end_formatted_UTC: string;

    if (this.appointment_info.type == 1) {
      const start_time = dayjs(this.appointment_info.selected_time_slot);
      const end_time = start_time.add(this.appointment_info.selected_length, 'minute');
      start_formatted_UTC = start_time.utc().format('YYYY-MM-DDTHH:mm:00.000') + 'Z';
      end_formatted_UTC = end_time.utc().format('YYYY-MM-DDTHH:mm:00.000') + 'Z';
    } else {
      const start_time_time = dayjs(this.appointment_info.start_time);
      const end_time_time = dayjs(this.appointment_info.end_time);
      const start_time = dayjs(this.appointment_info.date).add(start_time_time.get('hour'), 'hour').add(start_time_time.get('minute'), 'minute');
      const end_time = dayjs(this.appointment_info.date).add(end_time_time.get('hour'), 'hour').add(end_time_time.get('minute'), 'minute');
      start_formatted_UTC = start_time.utc().format('YYYY-MM-DDTHH:mm:00.000') + 'Z';
      end_formatted_UTC = end_time.utc().format('YYYY-MM-DDTHH:mm:00.000') + 'Z';
    }
    console.log("~~~~~~~~~~~~~");
    console.log("Local Start Time:", this.appointment_info.start_time);
    console.log("Local End Time:", this.appointment_info.end_time);
    console.log("Start Time (UTC):", start_formatted_UTC);
    console.log("End Time (UTC):", end_formatted_UTC);


    let data: any;

    if (this.event_guid && !this.email_guid) {
      // Rescheduling an existing calendar event (no email link)
      const body = {
        title: this.appointment_info.title,
        description: this.appointment_info.description,
        start_time: start_formatted_UTC,
        end_time: end_formatted_UTC,
        users: this.appointment_info.selected_users.map(guid => ({ guid }))
      };
      await this.globalService.presentLoading('Rescheduling event...', 99999999);
      data = await this.apiService.put(`calendar/events/${this.event_guid}`, body);
      this.globalService.dismissLoading();
      if (data?.success) {
        this.globalService.presentToastMessage('Appointment rescheduled!', true);
        this.modalController.dismiss({ refresh: true });
      } else {
        this.globalService.presentToastMessage(data?.message || 'Failed to reschedule appointment', false);
      }
      return;
    }

    if (this.email_guid) {
      const body = {
        users: this.appointment_info.selected_users.map(guid => ({ guid })),
        start_time_iso: start_formatted_UTC,
        end_time_iso: end_formatted_UTC,
        title: this.appointment_info.title,
        description: this.appointment_info.description,
        tz_str: Intl.DateTimeFormat().resolvedOptions().timeZone
      };
      console.log("Booking appointment with body:", body);
      await this.globalService.presentLoading(this.is_rescheduling ? 'Rescheduling appointment...' : 'Booking appointment...', 99999999);
      data = this.is_rescheduling
        ? await this.apiService.put(`emails/${this.email_guid}/appointment_request/book`, body)
        : await this.apiService.post(`emails/${this.email_guid}/appointment_request/book`, body);
      this.globalService.dismissLoading();
      if (data?.success) {
        this.globalService.presentToastMessage(this.is_rescheduling ? 'Appointment rescheduled!' : 'Appointment booked!', true);
        this.modalController.dismiss({ refresh: true });
      } else {
        this.globalService.presentToastMessage(data?.message || 'Failed to save appointment', false);
      }
      return;
    }

    const selected_users_guids = [];
    for (const tm of this.appointment_info.selected_users) {
      selected_users_guids.push({ guid: tm });
    }
    for (const client of this.appointment_info.assigned_clients) {
      selected_users_guids.push({ guid: client.guid });
    }
    for (const company of this.appointment_info.assigned_companies) {
      // Resolved server-side (default contact, or the default contact for company.role_guid if
      // one was picked) rather than trusting company.default_client here - that's always the
      // company-level default and ignores role (see Companies.GetDefaultClientGUIDForRole).
      selected_users_guids.push({ company_guid: company.guid, role_guid: company.role_guid || null });
    }
    const event_data = {
      title: this.appointment_info.title,
      description: this.appointment_info.description,
      start_time_iso: start_formatted_UTC,
      end_time_iso: end_formatted_UTC,
      type: this.appointment_info.type,
      users: selected_users_guids
    };
    await this.globalService.presentLoading('Creating event...', 99999999);
    data = await this.apiService.post('calendar/events', event_data);
    this.globalService.dismissLoading();
    if (data.success) {
      if (this.appointment_info.type == 1) this.globalService.presentToastMessage('Appointment Created', true);
      else if (this.appointment_info.type == 0) this.globalService.presentToastMessage('Blocked Time Created', true);
      this.modalController.dismiss({ refresh: true });
    }
  }

  async presentSelectClientModal() {
    const modal = await this.modalController.create({
      component: AddressBookComponent,
      cssClass: 'large-modal secondary-modal',
      backdropDismiss: false,
      componentProps: {
        external_selection_mode: true,
        allow_client_selection: true,
        allow_non_client_selection: true,
        allow_company_selection: true,
        allow_contact_selection: true,
        allow_company_group_selection: false,
        allow_contact_group_selection: false,
        allow_role_selection: false,
        in_selected_companies: this.appointment_info.assigned_companies,
        in_selected_contacts: this.appointment_info.assigned_clients,
        expanded_clients: true
      },
    })
    modal.onDidDismiss().then((data: any) => {
      this.appointment_info.assigned_clients = [];
      this.appointment_info.assigned_companies = [];
      if (data) {
        if (data.data) {
          data.data.forEach(element => {
            if (element.is_company) {
              this.appointment_info.assigned_companies.push(element);
            } else if (element.is_contact) {
              this.appointment_info.assigned_clients.push(element);
            }
          });
        }
      }
    });
    return await modal.present()
  }

  async RemoveClient(client) {
    this.appointment_info.assigned_clients.splice(this.appointment_info.assigned_clients.findIndex(item => item.guid === client.guid), 1);
  }

  async RemoveCompany(company) {
    this.appointment_info.assigned_companies.splice(this.appointment_info.assigned_companies.findIndex(item => item.guid === company.guid), 1);
  }

  UpdateLength() {
    this.appointment_info.selected_length = (this.appointment_info.in_hours * 60) + this.appointment_info.in_minutes;
    this.selectedLengthChange();
  }

}

